import { Box, Stack } from '@mui/material'
import { WarningIcon } from '@phosphor-icons/react'
import { InfoBox, tokens, User } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { UsersAutocomplete } from 'ComponentLibrary/src/widgets/Participants/UserAutocomplete'
import { useTranslation } from 'react-i18next'

type AppraiserSelectionSectionProps = {
    appraiserUsers: User[]
    selectedAppraiserId?: number
    setSelectedAppraiserId: (selectedId?: number) => void
    isAppraiserUsersPending?: boolean
    copy?: Partial<AppraiserSelectionSectionCopy>
}

export type AppraiserSelectionSectionCopy = {
    title: string
    description: string
    loading: string
    empty: string
    selectLabel: string
}

export function AppraiserSelectionSection({
    appraiserUsers,
    selectedAppraiserId,
    setSelectedAppraiserId,
    isAppraiserUsersPending,
    copy: copyOverrides,
}: AppraiserSelectionSectionProps) {
    const { t } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.appraiserSelectionSection',
    })
    const copy: AppraiserSelectionSectionCopy = {
        title: copyOverrides?.title ?? t('title'),
        description: copyOverrides?.description ?? t('description'),
        loading: copyOverrides?.loading ?? t('loading'),
        empty: copyOverrides?.empty ?? t('emptyState'),
        selectLabel: copyOverrides?.selectLabel ?? t('fieldLabel'),
    }
    const selectedAppraiser = appraiserUsers.find((appraiser) => appraiser.id === selectedAppraiserId) || null

    return (
        <Stack direction="column" gap={tokens.SizingSpacing150} width="100%">
            <Headline variant="headline4" title={copy.title} />

            <Typography variant="textS" color={tokens.ColorTextBold}>
                {copy.description}
            </Typography>

            {isAppraiserUsersPending ? (
                <Typography variant="textS" color={tokens.ColorTextDefault}>
                    {copy.loading}
                </Typography>
            ) : appraiserUsers.length === 0 ? (
                <InfoBox variant="warning" icon={<WarningIcon />}>
                    <Typography variant="textS" color={tokens.ColorTextBold}>
                        {copy.empty}
                    </Typography>
                </InfoBox>
            ) : (
                <Box width="100%" maxWidth="350px">
                    <UsersAutocomplete
                        users={appraiserUsers}
                        value={selectedAppraiser}
                        label={copy.selectLabel}
                        onChange={(user) => setSelectedAppraiserId(user?.id)}
                        required
                    />
                </Box>
            )}
        </Stack>
    )
}
